import axios from "axios";
import { useSnackbar } from "../components/snackbar";
import { useNavigate } from "react-router-dom";
import { useClassroomStore } from "../store/classroom-store";
import { Classroom, createClassroomPayload, Enrollment, Payout, updateClassroomPayload } from "../models/classroom";
import sanitizeString from "../utils/sanitize-name";
import { uploadResource } from "../utils/upload-resource";
import { Assessment, newAssessment, createAssessmentPayload, updatedAssessmentPayload, AssessmentStatus } from "../models/assessment";
import { Collection, CollectionStatus, updateCollectionPayload } from "../models/collection";
import { createResourcePayload, Resource, ResourceType, updateResourcePayload } from "../models/resource";
import qs from "qs";
import { Collaborator, createCollaboratorPayload, updateCollaboratorPayload } from "../models/collaborator";
import { Lecture, newLecturePayload, updateLecturePayload } from "../models/lecture";
import { useDomainActions } from "./useDomainActions";
import { Hub } from "../models/hub";

const API_URL = process.env.REACT_APP_API_URL;

export const useClassroomActions = () => {
    const { showMessage } = useSnackbar();

    const navigate = useNavigate();

    const { classrooms, setClassrooms, cachedResources, setCachedResources } = useClassroomStore();

    const { hub, setHub } = useDomainActions();
    
    const getClassrooms = async () => {        
        try {
            const response = await axios.get<Classroom[]>(
                `${API_URL}/instructor/classrooms`,
                { withCredentials: true }
            );
            
            const classroomsMap = response.data.reduce((acc, cls) => {
                acc[cls.id] = cls;
                return acc;
            }, {} as Record<number, Classroom>);
     
            
            setClassrooms(classroomsMap);    
        } catch (error) {        
            handleApiError(error);
        }
    };

    const createClassroom = async (classroom: Classroom) => {
        try {
            if (classroom.image_file) {
                classroom.image = sanitizeString(classroom.image_file.name);
            }

            const response = await axios.post<Classroom>(
                `${API_URL}/instructor/classrooms`,
                createClassroomPayload(classroom),
                { withCredentials: true }
            );

            const updatedClassroom = response.data;

            if (classroom.image_file) {
                const path = `classrooms/${updatedClassroom.id}/class_images/${updatedClassroom.image}`;
                await uploadResource(path, classroom.image_file, classroom.image_file.type);
            }

            setClassrooms({
                ...classrooms,
                [updatedClassroom.id]: updatedClassroom,
            });

            showMessage("Classroom Created", 3);
        } catch (error) {
            handleApiError(error);
        }
    };

    const updateClassroom = async (classroom: Classroom) => {    
        try {
            if (classroom.image_file) {
                classroom.image = sanitizeString(classroom.image_file.name);
            }

            const response = await axios.put<Classroom>(
                `${API_URL}/instructor/classrooms/${classroom.id}`,
                updateClassroomPayload(classroom),
                { withCredentials: true }
            );

            

            if (classroom.image_file) {
                const path = `classrooms/${classroom.id}/class_images/${classroom.image}`;
                await uploadResource(path, classroom.image_file, classroom.image_file.type);
            }

            // if (classroom.removed_image) {
            //     const path = `classrooms/${classroom.id}/class_images/${classroom.removed_image}`;
            //     await deleteResource(path);
            // }

            const _classrooms = useClassroomStore.getState().classrooms || {};

            setClassrooms({
                ..._classrooms,
                [classroom.id]: {
                    ..._classrooms[classroom.id],
                    name: response.data.name,
                    description: response.data.description,
                    image: response.data.image,
                    instructor_amount: response.data.instructor_amount,
                    enrollment_type: response.data.enrollment_type,
                    duration: response.data.duration,
                },
            });

            showMessage("Classroom Updated", 3);
        } catch (error) {
            handleApiError(error);
        }
    };

    const payWithClassroomEarnings = async (classroom_id: number, amount: number) => {
        try {
            const response = await axios.put<Hub>(
                `${API_URL}/instructor/classrooms/${classroom_id}/earnings`,
                { "amount": amount} ,
                { withCredentials: true }
            );
            
            const _classrooms = useClassroomStore.getState().classrooms || {};

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    earnings: _classrooms[classroom_id].earnings - amount
                }
            });
            
            setHub(response.data);

            showMessage("Hub Activated", 3);
        } catch (error) {
            handleApiError(error);
        }
    }

    const withdrawEarnings = async (classroom_id: number, amount: number, otp: string) => {
        try {
            const response = await axios.post<Payout>(
                `${API_URL}/instructor/classrooms/${classroom_id}/withdraw`,
                {
                    "amount": amount,
                    "otp": otp
                },
                { withCredentials: true }
            );
            
            const _classrooms = useClassroomStore.getState().classrooms || {};

            setClassrooms({
                ...classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    earnings: _classrooms[classroom_id].earnings - amount,
                    payouts: [response.data, ..._classrooms[classroom_id].payouts || []]
                }
            });

            showMessage("Withdrawal Queued", 3);
        } catch (error) {
            handleApiError(error);
        }
    }

    const getPayoutData = async (classroom_id: number, payout_id: number) => {
        try {
            const response = await axios.get<Payout>(
                `${API_URL}/instructor/classrooms/${classroom_id}/payouts/${payout_id}`,
                { withCredentials: true }
            );

            const updatedPayout = response.data;
            
            const _classrooms = useClassroomStore.getState().classrooms || {};
            const classroom = _classrooms[classroom_id] || {};

            const updatedPayouts = (classroom.payouts || []).map((p) =>
                p.id === payout_id ? updatedPayout : p
            );

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ...classroom,
                    payouts: updatedPayouts
                }
            });
        } catch (error) {
            handleApiError(error);
        }
    };


    const sendWithdrawalOtp = async (classroom_id: number, amount: string) => {
        try {
            await axios.post<Payout>(
                `${API_URL}/instructor/classrooms/${classroom_id}/withdraw/otp`,
                { amount: amount },
                { withCredentials: true }
            );
        
            showMessage("OTP Sent", 3);
        } catch (error) {
            handleApiError(error);
        }
    } 

    const deleteClassroom = async (classroom_id: number, password: string) => {
        try {
            await axios.delete(
                `${API_URL}/instructor/classrooms/${classroom_id}`,
                { data: {password}, withCredentials: true }
            );
            
            const _classrooms = useClassroomStore.getState().classrooms || {};
            
            const { [classroom_id]: _, ...updatedClassrooms } = _classrooms;

            setClassrooms(updatedClassrooms);
            
            showMessage("Classroom Deleted", 3);
        } catch (error) {
            handleApiError(error);
        }
    };

    const getLectures = async (classroom_id: number) => {
        try {
            const response = await axios.get<Lecture[]>(
                `${API_URL}/instructor/classrooms/${classroom_id}/lectures`,
                { withCredentials: true }
            );

            const lecturesMap = response.data.reduce((lec, lecture) => {
                lec[lecture.id] = lecture;
                return lec;
            }, {} as Record<number, Lecture>);

            const _classrooms = useClassroomStore.getState().classrooms || {};

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    lectures: lecturesMap
                }
            });
        } catch (error) {
            handleApiError(error);
        }
    };


    const createLecture = async (classroom_id: number, lecture: Lecture) => {
        try {
            const _classrooms = useClassroomStore.getState().classrooms || {};

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    lectures: {
                        ..._classrooms[classroom_id].lectures,
                        [lecture.id]: lecture
                    }
                }
            });

            lecture.expected_students = classrooms?.[classroom_id].enrollment_count || 100;
            const response = await axios.post<Lecture>(
                `${API_URL}/instructor/classrooms/${classroom_id}/lectures`,
                newLecturePayload(classroom_id, lecture),
                { withCredentials: true }
            );

           
            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    lectures: {
                        ..._classrooms[classroom_id].lectures,
                        [response.data.id]: response.data
                    }
                }
            });
            showMessage("Lecture Created", 3);
        } catch (error) {
            handleApiError(error);
        }
    };


    const getLectureById = async (classroom_id: number, lecture_id: number) => {
        try {
            const response = await axios.get<Lecture>(
                `${API_URL}/instructor/classroom/${classroom_id}/lectures/${lecture_id}`,
                { withCredentials: true }
            );

            const _classrooms = useClassroomStore.getState().classrooms || {};

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    lectures: {
                        ..._classrooms[classroom_id].lectures,
                        [lecture_id]: response.data
                    }
                }
            });
        } catch (error) {
            handleApiError(error);
        }
    };

    const updateLecture = async (classroom_id: number, lecture: Lecture) => {
        try {
            const response = await axios.put<Lecture>(
                `${API_URL}/instructor/classrooms/${classroom_id}/lectures/${lecture.id}`,
                updateLecturePayload(lecture),
                { withCredentials: true }
            );

            const _classrooms = useClassroomStore.getState().classrooms || {};

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    lectures: {
                        ..._classrooms[classroom_id].lectures,
                        [response.data.id]: response.data
                    }
                }
            });
            showMessage("Lecture Updated", 3);
        } catch (error) {
            handleApiError(error);
        }
    };


    const deleteLecture = async (classroom_id: number, lecture_id: number) => {
        try {
            await axios.delete(
                `${API_URL}/instructor/classrooms/${classroom_id}/lectures/${lecture_id}`,
                { withCredentials: true }
            );

            const _classrooms = useClassroomStore.getState().classrooms || {};

            const lectures = _classrooms[classroom_id].lectures || {};

            const currentLecture = lectures[lecture_id];

            if (!currentLecture) return;

            const { [lecture_id]: _, ...newLectures } = lectures;

            setClassrooms({ 
                ...classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    lectures: newLectures
                }
            });    
            showMessage("Lecture Deleted", 3);
        } catch (error) {
            handleApiError(error);
        }
    }


    const getAssessments = async (classroom_id: number) => {  
        try {
            const response = await axios.get<Assessment[]>(
                `${API_URL}/instructor/classrooms/${classroom_id}/assessments`,
                { withCredentials: true }
            );
        
            const assessmentsMap = response.data.reduce((acc, assessment) => {
                acc[assessment.id] = assessment;
                return acc;
            }, {} as Record<number, Assessment>);
            
            
            const _classrooms = useClassroomStore.getState().classrooms || {};
            
            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    assessments: assessmentsMap,
                },
            });
            
        } catch (error) {
            handleApiError(error);
        }
    };

    const getAssessment = async (classroom_id: number, assessment_id: number) => {
        try {        
            const response = await axios.get<Assessment>(
                `${API_URL}/instructor/classrooms/${classroom_id}/assessments/${assessment_id}`,
                { withCredentials: true }
            );
            
            const _classrooms = useClassroomStore.getState().classrooms || {};
    
            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ...( _classrooms[classroom_id] || {} ),
                    assessments: {
                        ...( _classrooms[classroom_id]?.assessments || {} ),
                        [assessment_id]: response.data
                    }
                }
            });
        } catch (err) {
            handleApiError(err);
        }
    };
  
    const createAssessment = async (classroom_id: number, assessment: Assessment) => {
        try {
            const _classrooms = useClassroomStore.getState().classrooms || {};

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    assessments: {
                        ..._classrooms[classroom_id].assessments,
                        [assessment.id]: assessment,
                    },
                },
            });

            const response = await axios.post<Assessment>(
                `${API_URL}/instructor/classrooms/${classroom_id}/assessments`,
                createAssessmentPayload(assessment, classroom_id),
                { withCredentials: true }
            );

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    assessments: {
                        ..._classrooms[classroom_id].assessments,
                        [response.data.id]: response.data,
                    },
                },
            });
          

            showMessage("Assessment Created", 3);

        } catch (error) {
            handleApiError(error);
        }
    };

    const updateAssessment = async (classroom_id: number, assessment: Assessment) => {
        try {
            const response = await axios.put<Assessment>(
                `${API_URL}/instructor/classrooms/${classroom_id}/assessments/${assessment.id}`,
                updatedAssessmentPayload(assessment, classroom_id),
                { withCredentials: true }
            );
            
            const _classrooms = useClassroomStore.getState().classrooms || {};
            const existingAssessment = _classrooms[classroom_id]?.assessments?.[assessment.id] || newAssessment();

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    assessments: {
                        ..._classrooms[classroom_id].assessments,
                        [assessment.id]: {
                            ...response.data,
                            responses: existingAssessment.responses || []  
                        }
                    }
                }
            });

        } catch (error) {
            handleApiError(error);
        }
    };

    const updateAssessmentStatus = async (classroom_id: number, assessment_id: number, newStatus: AssessmentStatus) => {
        try {
            const response = await axios.put<AssessmentStatus>(
                `${API_URL}/instructor/classrooms/${classroom_id}/assessments/${assessment_id}/status`,
                {
                    id: assessment_id,
                    status: newStatus
                },
                { withCredentials: true }
            );
            
            const _classrooms = useClassroomStore.getState().classrooms || {};
            const existingAssessment = _classrooms[classroom_id]?.assessments?.[assessment_id] || newAssessment();

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    assessments: {
                        ..._classrooms[classroom_id].assessments,
                        [assessment_id]: {
                            ...existingAssessment,
                            status: response.data
                        }
                    }
                }
            });

        } catch (error) {
            console.error(error);
        }
    };

    const getAssessmentResponses = async (classroom_id: number, assessment_id: number, reset: boolean) => {
        try {
            const _classrooms = useClassroomStore.getState().classrooms || {};
            
            const classroom = _classrooms[classroom_id] || {};

            const assessments = classroom.assessments || {};

            const assessment = assessments[assessment_id] || {};
            const last_id = reset ? 0 : assessment.responses && assessment.responses.length > 0 ? assessment.responses[assessment.responses.length - 1].id : 0;
            const response = await axios.get<any[]>(
                `${API_URL}/instructor/classrooms/${classroom_id}/assessments/${assessment_id}/responses?last_id=${last_id}`,
                { withCredentials: true }
            );
            
            
            const responses = reset ? response.data : [...assessment.responses || [], ...response.data]
            
            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ...classroom,
                    assessments: {
                        ...assessments,
                        [assessment_id]: {
                            ...assessment,
                            responses: responses
                        }
                    }
                }
            });
            
            return response.data.length;
        } catch (error) {
            handleApiError(error);
        }
    };

    const getAssessmentResult = async (submission_id: number) => {
        try {
            const response = await axios.get(`${API_URL}/instructor/submissions/${submission_id}`,
                { withCredentials: true }
            );
            return response.data;
        } catch (error) {
            handleApiError(error);
        }
    };

    const deleteAssessmentResponses = async (classroom_id: number, assessment_id: number, responses_id: number[]) => {
        try {
            const query = qs.stringify(
                { responses_id }, 
                { arrayFormat: 'repeat' }
            );

            await axios.delete(
                `${API_URL}/instructor/classrooms/${classroom_id}/assessments/${assessment_id}/responses?${query}`,
                { withCredentials: true }
            );
            
            const _classrooms = useClassroomStore.getState().classrooms || {};
            const classroom = _classrooms[classroom_id] || {};
            const assessments = classroom.assessments || {};
            const assessment = assessments[assessment_id] || {};
            const updatedResponses = assessment.responses?.filter(
                (res) => !responses_id.includes(res.id)
            ) ?? [];

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ...classroom,
                    assessments: {
                        ...assessments,
                        [assessment_id]: {
                            ...assessment,
                            responses: updatedResponses
                        }
                    }
                }
            });

            showMessage(`Deleted ${responses_id.length} responses`, 3);
        } catch (error) {
            handleApiError(error);
        }
    };


    const deleteAssessment = async (classroom_id: number, assessment_id: number) => {
        try {
            await axios.delete(
                `${API_URL}/instructor/classrooms/${classroom_id}/assessments/${assessment_id}`,
                { withCredentials: true }
            );

            const _classrooms = useClassroomStore.getState().classrooms || {};

            const assessments = _classrooms[classroom_id].assessments || {};

            const currentAssessment = assessments[assessment_id];

            if (!currentAssessment) return;

            const { [assessment_id]: _, ...newAssessments } = assessments;

            setClassrooms({ 
                ...classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    assessments: newAssessments
                }
            });    

            showMessage("Assessment Deleted", 3);
        } catch (error) {
            handleApiError(error);
        }
    };

    const getCollections = async (classroom_id: number) => { 
        try {
            const response = await axios.get<Collection[]>(
                `${API_URL}/instructor/classrooms/${classroom_id}/collections`,
                { withCredentials: true }
            );

            const collectionsMap = response.data.reduce((col, collection) => {
                col[collection.id] = collection;
                return col;
            }, {} as Record<number, Collection>);
            
            const _classrooms = useClassroomStore.getState().classrooms || {};

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    collections: collectionsMap
                }
            });
        } catch (error) {
            handleApiError(error);
        }
    };
  
    const createCollection = async (classroom_id: number, collection: Collection) => {
        try {
            const _classrooms = useClassroomStore.getState().classrooms || {};

            setClassrooms({
                ...classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    collections: {
                        ..._classrooms[classroom_id].collections,
                        [collection.id]: collection
                    }           
                }
            });

            const response = await axios.post<Collection>(
                `${API_URL}/instructor/classrooms/${classroom_id}/collections`,
                { 'title': collection.title, 'classroom_id': classroom_id},
                { withCredentials: true }
            );

            setClassrooms({
                ...classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    collections: {
                        ..._classrooms[classroom_id].collections,
                        [response.data.id]: response.data
                    }           
                }
            });

            showMessage("Collection Created", 3);
        } catch (error) {
            handleApiError(error);
        }
    };

    const updateCollection = async (classroom_id: number, collection: Collection) => {
        try {
            const response = await axios.put<Collection>(
                `${API_URL}/instructor/classrooms/${classroom_id}/collections/${collection.id}`,
                updateCollectionPayload(collection),
                { withCredentials: true }
            );

            const _classrooms = useClassroomStore.getState().classrooms || {};

            const collections = _classrooms[classroom_id].collections || {};
            
            const currentCollection = collections[collection.id];

            if (!currentCollection) return;

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    collections : {
                        ...collections,
                        [collection.id]: {
                            ...currentCollection,
                            title: response.data.title,
                            description: response.data.description,
                            status: response.data.status,
                        }
                    }
                }
            });

            showMessage("Collection Updated", 3);
        } catch (error) {
            handleApiError(error);
        }
    };

    const updateCollectionStatus = async (classroom_id: number, collection_id: number, newStatus: CollectionStatus) => {
        try {
            const response = await axios.put<CollectionStatus>(
                `${API_URL}/instructor/classrooms/${classroom_id}/collections/${collection_id}/status`,
                {
                    id: collection_id,
                    status: newStatus
                },
                { withCredentials: true }
            );

            const _classrooms = useClassroomStore.getState().classrooms || {};

            const collections = _classrooms[classroom_id].collections || {};
            
            const currentCollection = collections[collection_id];

            if (!currentCollection) return;

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    collections : {
                        ...collections,
                        [collection_id]: {
                            ...currentCollection,
                            status: response.data,
                        }
                    }
                }
            });

            showMessage("Collection Updated", 3);
        } catch (error) {
            handleApiError(error);
        }
    };

    const deleteCollection = async (classroom_id: number, collection_id: number) => {
        try {
            await axios.delete(`${API_URL}/instructor/classrooms/${classroom_id}/collections/${collection_id}`,
                { withCredentials: true }
            );

            const _classrooms = useClassroomStore.getState().classrooms || {};

            const collections = _classrooms[classroom_id].collections || {};
                
            const { [collection_id]: _, ...newCollections } = collections;

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    collections: newCollections
                }
            });   

            showMessage("Collection Deleted", 3);
        } catch (error) {
            handleApiError(error);
        }
    };
  
    const createResource = async (classroom_id: number, collection_id: number, resource: Resource, onProgress?: (percent: number) => void) => {
        try {
            let _classrooms = useClassroomStore.getState().classrooms || {};
        
            let collections = _classrooms[classroom_id].collections || {};
            
            let currentCollection = collections[collection_id];


            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    collections: {
                        ...collections,
                        [collection_id]: {
                            ...currentCollection,
                            resources: {
                                ...currentCollection.resources,
                                [resource.id]: resource
                            }
                        }
                    }
                }
            });

            const response = await axios.post<Resource>(
                `${API_URL}/instructor/classrooms/${classroom_id}/collections/${collection_id}/resources`,
                createResourcePayload(resource),
                { withCredentials: true }
            );

            const updatedResource = response.data;

           if (resource.file) {
                const path = `classrooms/${classroom_id}/resources/${resource.url}`;
                const resource_data = resource.type === ResourceType.NOTE ? JSON.stringify(null) : resource.file;
                const resource_type = resource.type === ResourceType.NOTE ? "application/json" : resource.file?.type;

                await uploadResource(path, resource_data, resource_type, (percent) => {
                    if (onProgress) onProgress(percent);
                });
           }

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    collections: {
                        ...collections,
                        [collection_id]: {
                            ...currentCollection,
                            resources: {
                                ...currentCollection.resources,
                                [updatedResource.id]: updatedResource
                            }
                        }
                    }
                }
            });

            showMessage("Resource Created", 3)
        } catch (error) {
            handleApiError(error);
        }
    };

    const getResources = async (classroom_id: number, collection_id: number) => {
        try {
        
            const response = await axios.get<Resource[]>(
                `${API_URL}/instructor/classrooms/${classroom_id}/collections/${collection_id}/resources`,
                { withCredentials: true }
            );

            const resourcesMap = response.data.reduce((res, resource) => {
                res[resource.id] = resource;
                return res;
            }, {} as Record<number, Resource>);

            const _classrooms = useClassroomStore.getState().classrooms || {};

            const existingClassroom = _classrooms[classroom_id] || {};

            const collections = existingClassroom.collections || {};

            const currentCollection = collections[collection_id] || {};

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ...existingClassroom,
                    collections: {
                        ...collections,
                        [collection_id]: {
                            ...currentCollection,
                            resources: resourcesMap
                        }
                    }
                }
            });
        } catch (err) {
            handleApiError(err);
        }
    };

    const updateResource = async (classroom_id: number, collection_id: number, resource: Resource, onProgress?: (percent: number) => void) => {    
        try {
            const response = await axios.put<Resource>(
                `${API_URL}/instructor/classrooms/${classroom_id}/collections/${collection_id}/resources/${resource.id}`,
                updateResourcePayload(resource),
                { withCredentials: true }
            );

            if (resource.file) {
                const path = `classrooms/${classroom_id}/resources/${resource.url}`;
                await uploadResource(path, resource.file, resource.file.type, (percent) => {
                    if (onProgress) onProgress(percent);
                });
            }
        
            // if (resource.removed_resource) {
            //     const path = `classrooms/${classroom_id}/resources/${resource.removed_resource}`;
            //     await deleteResource(path)
            // }

            const _classrooms = useClassroomStore.getState().classrooms || {};

            const collections = _classrooms[classroom_id].collections || {};
            
            const currentCollection = collections[collection_id];

            const currentResources = currentCollection.resources || {};

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    collections: {
                        ...collections,
                        [collection_id]: {
                            ...currentCollection,
                            resources: {
                                ...currentResources,
                                [resource.id]: response.data
                            }
                        }
                    }
                }
            });
            showMessage("Resource Updated", 3);
        } catch (error) {
            handleApiError(error);
        }
    };

    const deleteResource = async(classroom_id: number, collection_id: number, resource_id: number) => {
        try {
            await axios.delete(
                `${API_URL}/instructor/classrooms/${classroom_id}/collections/${collection_id}/resources/${resource_id}`,
                { withCredentials: true });

            const _classrooms = useClassroomStore.getState().classrooms || {};

            const collections = _classrooms[classroom_id].collections || {};
            
            const currentCollection = collections[collection_id];

            const currentResources = currentCollection.resources || {};

            const { [resource_id]: _, ...newResources } = currentResources;

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    collections: {
                        ...collections,
                        [collection_id]: {
                            ...currentCollection,
                            resources: newResources
                        }
                    }
                }
            });  
            showMessage("Resource Deleted", 3);
        } catch (error) {
            handleApiError(error);
        }
    };

    const getStudents = async (classroom_id: number, reset: boolean) => {

        const _classrooms = useClassroomStore.getState().classrooms || {};
        const classroom = _classrooms[classroom_id] || {};
        
        const last_id = reset ? 0 : (classroom.enrollments && classroom.enrollments.length > 0) ? classroom.enrollments[classroom.enrollments.length - 1].id : 0;

        try {
            const response = await axios.get<Enrollment[]>(
                `${API_URL}/instructor/classrooms/${classroom_id}/students?last_id=${last_id}`,
                { withCredentials: true }
            );

            const enrollments = reset ? response.data : [...classroom.enrollments || [], ...response.data];
            
            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ...classroom,
                    enrollments: enrollments
                }
            });

            return response.data.length;
        } catch (error) {
            handleApiError(error);;
        }
    };

    const unEnrollStudents = async (classroom_id: number, students_id: number[]) => {
        try {
            const query = qs.stringify(
                { students_id }, 
                { arrayFormat: 'repeat' }
            );

            await axios.delete(
                `${API_URL}/instructor/classrooms/${classroom_id}/students?${query}`,
                { withCredentials: true }
            );
            
            const _classrooms = useClassroomStore.getState().classrooms || {};

            setClassrooms({
                ...classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    enrollment_count: _classrooms[classroom_id].enrollment_count - students_id.length,
                    enrollments: (_classrooms[classroom_id].enrollments || []).filter(
                        (enrollment) => !students_id.includes(enrollment.student_id)
                    )
                }
            });

            showMessage(`Un-Enrolled ${students_id.length} Students`, 3);
        } catch (error) {
            handleApiError(error);
        }
    };

    const getPayouts = async (classroom_id: number, offset: number) => {
        try {
            const response = await axios.get<Payout[]>(
                `${API_URL}/instructor/classrooms/${classroom_id}/payouts?offset=${offset}`,
                { withCredentials: true }
            );

            const _classrooms = useClassroomStore.getState().classrooms || {};
            const classroom = _classrooms[classroom_id] || {};
            const payouts = [...classroom.payouts || [], ...response.data];


            setClassrooms({
                ...classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    payouts: payouts
                }
            });

            return response.data.length;
        } catch (error) {
            handleApiError(error);
        }
    };

    const getImageResources = async (
        classroom_id: number,
        offset: number,
        newBatch: boolean = false
    ): Promise<[string, string][]> => {

        if (cachedResources && !newBatch) return cachedResources;

        try {
            const response = await axios.get<Resource[]>(
            `${API_URL}/instructor/classrooms/${classroom_id}/collections/resources/images?offset=${offset}`,
            { withCredentials: true }
            );

            const newResources: [string, string][] = [];

            if (response.data?.length) {
                for (const res of response.data) {
                    if (res.type === ResourceType.MEDIA && !res.url.endsWith(".mp4")) {
                        newResources.push([
                            res.title,
                            `https://d1at3arxf72ma9.cloudfront.net/classrooms/${classroom_id}/resources/${res.url}`,
                        ]);
                    }
                }
                const updatedResources = newBatch
                    ? [...(cachedResources ?? []), ...newResources]
                    : newResources;

                setCachedResources(updatedResources);
                return newResources;
            }

            return [];
        } catch (error) {
            handleApiError(error);
            return [];
        }
    };


    const getImageResourcesByTitle = async (classroom_id: number, title: string) => {
        try {
            let resources: [string, string][] = [];
            const response = await axios.get<Resource[]>(
                `${API_URL}/instructor/classrooms/${classroom_id}/collections/resources/images/by-title?title=${title}`,
                { withCredentials: true }
            );

            for (let i = 0; i < response.data.length; i++) {
                const resource: Resource = response.data[i];
                resources.push([
                    resource.title,
                    `https://d1at3arxf72ma9.cloudfront.net/classrooms/${classroom_id}/resources/${resource.url}`,
                ]);
            }
            
            return resources;
        } catch (error) {
            handleApiError(error);
        }
    };

    const getCollaborators = async (classroom_id: number) => {
        try {
            const response = await axios.get<Collaborator[]>(`${API_URL}/instructor/classrooms/${classroom_id}/collaborators`,
                { withCredentials: true }
            );

            const collaboratorsMap = response.data.reduce((acc, collab) => {
                acc[collab.id] = collab;
                return acc;
            }, {} as Record<number, Collaborator>);
            
            const _classrooms = useClassroomStore.getState().classrooms || {};

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    collaborators: collaboratorsMap
                }
            });

        } catch (error) {
            handleApiError(error);
        }
    };

    const createCollaborator = async (classroom_id: number, collaborator: Collaborator) => {
        try {
            const response = await axios.post<Collaborator>(
                `${API_URL}/instructor/classrooms/${classroom_id}/collaborators`, 
                createCollaboratorPayload(collaborator),
                { withCredentials: true }
            );  

            const _classrooms = useClassroomStore.getState().classrooms || {};

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    collaborators: {
                        ..._classrooms[classroom_id].collaborators,
                        [response.data.id]: response.data
                    }
                }
            });
            showMessage("Collaborator Created", 3);
        } catch (error) {
            handleApiError(error);
        }
    };

    const updateCollaborator = async (classroom_id: number, collaborator: Collaborator) => {
        try {
            const response = await axios.put<Collaborator>(
                `${API_URL}/instructor/classrooms/${classroom_id}/collaborators/${collaborator.id}`, 
                updateCollaboratorPayload(collaborator, Number(classroom_id)),
                { withCredentials: true }
            );  
        
            const _classrooms = useClassroomStore.getState().classrooms || {};

            const _collaborator = response.data;
            
            const currentCollaborators = _classrooms[classroom_id].collaborators || {};

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    collaborators: {
                        ...currentCollaborators,
                        [collaborator.id]: _collaborator
                    }
                }
            });

            showMessage("Collaborator Updated", 3);
        } catch (error) {
            handleApiError(error);
        }
    };

    const deleteCollaborator = async (classroom_id: number, collaborator_id: number) => {
        try {
            const response = await axios.delete<Collaborator>(
                `${API_URL}/instructor/classrooms/${classroom_id}/collaborators/${collaborator_id}`, 
                { withCredentials: true }
            );  

            const _classrooms = useClassroomStore.getState().classrooms || {};

            const currentCollaborators = _classrooms[classroom_id].collaborators || {};

            const { [collaborator_id]: _, ...updatedCollaborators } = currentCollaborators;

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ..._classrooms[classroom_id],
                    collaborators: updatedCollaborators
                }
            });

            showMessage("Collaborator Deleted", 3);
        } catch (error) {
            console.error()
        }
    };

    const getHubOverview = async () => {
        if (!classrooms) await getClassrooms();

        if (!classrooms) return;
        
        let totalStudents = 0, totalEarnings = 0;

        let enrollmentStat: { name: string; value: number }[] = [];

        Object.entries(classrooms).forEach(([key, value]) => {
            totalStudents += value.enrollment_count || 0;
            totalEarnings += value.earnings;

            enrollmentStat.push({"name": value.name, "value": value.enrollment_count})
        });
        
        return ({
            "totalStudents": totalStudents,
            "totalEarnings": totalEarnings,
            "totalClassrooms": Object.values(classrooms).length,
            "enrollmentStat": enrollmentStat
        });
    }

    const handleApiError = (error: any) => {
        if (error?.response?.status === 401) {
            navigate("/sign-in");
            return;
        }

        const message =
            error?.response?.data?.detail ||
            error?.response?.data?.message ||
            error.message ||
            "An unexpected error occurred";

        showMessage(message, 0);
    };
    
    return {
        classrooms,
        getHubOverview,
        getClassrooms,
        createClassroom,
        updateClassroom,
        withdrawEarnings,
        sendWithdrawalOtp,
        payWithClassroomEarnings,
        deleteClassroom,
        getLectures,
        createLecture,
        getLectureById,
        updateLecture,
        deleteLecture,
        getAssessments,
        getAssessment,
        createAssessment,
        updateAssessment,
        updateAssessmentStatus,
        getAssessmentResponses,
        getAssessmentResult,
        deleteAssessmentResponses,
        deleteAssessment,
        getCollections,
        createCollection,
        updateCollection,
        updateCollectionStatus,
        deleteCollection,
        createResource,
        getResources,
        updateResource,
        deleteResource,
        getStudents,
        unEnrollStudents,
        getPayouts,
        getImageResources,
        getImageResourcesByTitle,
        getCollaborators,
        createCollaborator,
        updateCollaborator,
        deleteCollaborator,
        getPayoutData
    };
};
